import React, { useEffect, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import {
  selectAllUsersFromState,
  selectCreditCardsFullPaginatedData,
  selectFilteredCreditCards,
  selectUsersFullPaginatedData,
} from 'src/app/Budgets/redux/budgets.selectors'
import BackgroundPageContent from 'src/components/BackgroundPageContent/BackgroundPageContent'
import BudgetsBoxWithFilters, {
  BudgetsAnalyticsEventProperties,
  BudgetsFiltersBody,
} from 'src/components/BudgetsBoxWithFilters/BudgetsBoxWithFilters'
import LoadingBackgroundPage from 'src/components/LoadingBackgroundPage/LoadingBackgroundPage'
import ObjectBoxTitle from 'src/components/ObjectBoxTitle/ObjectBoxTitle'
import { UserDropdownInfiniteScrollProps } from 'src/components/UserDropdown/UserDropdown'
import { appStatisticsKeys } from 'src/globalDebouncedDispatcher'
import { useBackgroundDispatch, useQueryParamArrayHandler } from 'src/hooks'
import Trns from 'src/localization/Trns'
import { CreditCardType } from 'src/models/creditCard.models'
import { getCreditCards } from 'src/redux/actions/data/creditCards.actions'
import { getUsers } from 'src/redux/actions/data/users.actions'
import {
  selectAllStatisticsLoadedFromState,
  selectStatisticsDispatcherFromState,
} from 'src/redux/selectors/app.selectors'
import {
  selectCreditCardsActiveDelegateCountStatisticsFromState,
  selectCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  selectCreditCardsDelegateUserMeCountStatisticsFromState,
} from 'src/redux/selectors/data/statistics.selectors'
import {
  selectMyWalletBudgetsMatch,
  selectQueryParams,
} from 'src/redux/selectors/router.selectors'
import { selectAppName } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

const BudgetsContent: React.FC = () => {
  const statisticsLoaded = useSelector(selectAllStatisticsLoadedFromState)
  const creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount = useSelector(
    selectCreditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsFromState,
  )
  const creditCardsActiveDelegateCount = useSelector(
    selectCreditCardsActiveDelegateCountStatisticsFromState,
  )
  const creditCardsDelegateUserMeCount = useSelector(
    selectCreditCardsDelegateUserMeCountStatisticsFromState,
  )
  const creditCardsFullPaginatedData = useSelector(
    selectCreditCardsFullPaginatedData,
  )
  const filteredCreditCards = useSelector(selectFilteredCreditCards)
  const appName = useSelector(selectAppName)
  const usersFullPaginatedData = useSelector(selectUsersFullPaginatedData)
  const recipientOptions = useSelector(selectAllUsersFromState)
  const queryParams = useSelector(selectQueryParams)
  const dispatchStatistics = useSelector(selectStatisticsDispatcherFromState)
  const budgetsYourBudgetsMatch = useSelector(selectMyWalletBudgetsMatch)

  const dispatch = useBackgroundDispatch()

  const { analytics } = useAnalyticsContext()

  const [selectedFilters, setSelectedFilters] = useState<
    BudgetsAnalyticsEventProperties | undefined
  >(undefined)
  const [clientHeight, setClientHeight] = useState<number | null>(null)
  const ref: React.MutableRefObject<HTMLDivElement | null> = useRef(null)

  const [viewportHeight, setViewportHeight] = useState({
    height: window.innerHeight,
  })

  const updateWindowSize = (): void => {
    setViewportHeight({ height: window.innerHeight })
  }

  useEffect(() => {
    setClientHeight(viewportHeight.height - 122)
  }, [ref?.current])

  useEffect(() => {
    window.addEventListener('resize', updateWindowSize)
    setClientHeight(viewportHeight.height - 122)
    return (): void => {
      window.removeEventListener('resize', updateWindowSize)
    }
  }, [viewportHeight, ref?.current])

  useEffect((): void => {
    dispatchStatistics([
      appStatisticsKeys.CreditCardsActiveDelegateCountStatistics,
      appStatisticsKeys.CreditCardsDelegateUserMeCountStatistics,
    ])
  }, [dispatchStatistics])

  const fetchBudgets = (
    filtersBody?: BudgetsFiltersBody,
    page?: number,
  ): void => {
    dispatch(
      getCreditCards({
        count: 10,
        page,
        type: CreditCardType.Delegate,
        sortField: 'activeUpdatedAt',
        ...(budgetsYourBudgetsMatch && { user: 'me' }),
        ...filtersBody,
      }),
    )
  }

  const infiniteScrollProps = {
    ...creditCardsFullPaginatedData,
    fetch: fetchBudgets,
  }

  const fetchUsers = (filterInput?: string, page?: number): void => {
    dispatch(
      getUsers({
        q: filterInput,
        count: 10,
        page: page,
      }),
    )
  }

  const userInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: fetchUsers,
    ...usersFullPaginatedData,
  }

  const totalBudgets = budgetsYourBudgetsMatch
    ? creditCardsDelegateUserMeCount
    : creditCardsActiveDelegateCount
  // analytics
  useEffect(() => {
    if (
      selectedFilters &&
      Object.values(selectedFilters).some(
        (arr) => arr !== undefined && arr.length > 0,
      )
    ) {
      analytics?.track(
        `${budgetsYourBudgetsMatch ? 'your' : 'all'} budgets filter applied`,
        {
          status: selectedFilters.status && selectedFilters.status.length > 0,
          budget_user:
            selectedFilters.budget_user &&
            selectedFilters.budget_user.length > 0,
          budget_creator:
            selectedFilters.budget_creator &&
            selectedFilters.budget_creator.length > 0,
        },
      )
    }
  }, [selectedFilters])

  return !(
    statisticsLoaded.creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCountStatisticsLoaded &&
    statisticsLoaded.creditCardsActiveDelegateCountStatisticsLoaded &&
    statisticsLoaded.creditCardsDelegateCountStatisticsLoaded &&
    statisticsLoaded.creditCardsDelegateUserMeCountStatisticsLoaded
  ) ? (
    <LoadingBackgroundPage />
  ) : (
    <>
      <Helmet>
        <title>{`Budgets | ${appName}`}</title>
        <meta name="description" content={`Budgets page of ${appName}`} />
      </Helmet>
      <div
        css={css`
          height: 100%;
        `}
        ref={ref}
        id={'ref'}
      >
        <BackgroundPageContent noStackingIndex noPadding>
          <BudgetsBoxWithFilters
            budgets={filteredCreditCards}
            isSlim={false}
            totalBudgets={totalBudgets}
            defaultTitle={
              <ObjectBoxTitle
                static={{
                  label: <Trns source={'Active'} />,
                  count: totalBudgets,
                }}
              />
            }
            infiniteScrollProps={infiniteScrollProps}
            clientHeight={clientHeight || 0}
            readOnly={
              creditCardsActiveSourceBudgetsEnabledCanIssueBudgetsCount === 0
            }
            userInfiniteScrollProps={userInfiniteScrollProps}
            recipientOptions={recipientOptions}
            initialFilterValues={{
              users: useQueryParamArrayHandler(queryParams.users),
              owners: useQueryParamArrayHandler(queryParams.owners),
              statuses: useQueryParamArrayHandler(queryParams.statuses),
            }}
            setSelectedFilters={setSelectedFilters}
          />
        </BackgroundPageContent>
      </div>
    </>
  )
}

export default BudgetsContent
